import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Loader2, Check } from 'lucide-react';
import { CampaignStatus, CampaignStatusLabels } from '@monitoring-panel/types';
import { StatusBadge } from './StatusBadge';
import { cn } from '@/lib/utils';

interface StatusSelectProps {
  status: CampaignStatus;
  onChange: (status: CampaignStatus) => void;
  isPending?: boolean;
  disabled?: boolean;
}

const STATUS_OPTIONS: CampaignStatus[] = [
  CampaignStatus.PendingReview,
  CampaignStatus.PendingSetup,
  CampaignStatus.PendingActivation,
  CampaignStatus.InProgress,
  CampaignStatus.Paused,
  CampaignStatus.Done,
  CampaignStatus.Validated,
  CampaignStatus.Discarded,
];

/**
 * Editable status badge. Renders the current status as a badge that opens a
 * dropdown of every lifecycle status; picking one calls onChange (which writes
 * it back to advia-os). Mirrors the click-outside / escape behaviour used by
 * the other dropdowns in the app.
 */
export function StatusSelect({ status, onChange, isPending, disabled }: StatusSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleSelect = (next: CampaignStatus) => {
    setIsOpen(false);
    if (next !== status) onChange(next);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && !isPending && setIsOpen((v) => !v)}
        disabled={disabled || isPending}
        title="Change status"
        className={cn(
          'inline-flex items-center gap-1 rounded-adv-pill transition-opacity focus-visible:outline-none focus-visible:shadow-adv-focus',
          'hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-60',
          !disabled && 'cursor-pointer'
        )}
      >
        <StatusBadge status={status} />
        {isPending ? (
          <Loader2 size={14} className="animate-spin text-adv-tertiary" />
        ) : (
          <ChevronDown
            size={14}
            className={cn('text-adv-tertiary transition-transform', isOpen && 'rotate-180')}
          />
        )}
      </button>

      {isOpen && (
        <div className="adv-dropdown absolute left-0 top-full mt-1 w-56 z-50">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => handleSelect(option)}
              className={cn(
                'adv-dropdown__item justify-between gap-2',
                option === status && 'adv-dropdown__item--active'
              )}
            >
              <span>
                <span className="adv-num">{option}.</span> {CampaignStatusLabels[option]}
              </span>
              {option === status && <Check size={14} className="text-adv-accent" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
